'use client';

import presetReact from '@bbob/preset-react';
import BB from '@bbob/react';
import Link from 'next/link';
import { Blockquote, Code, Divider, Image, List, Table, Text, Title } from '@mantine/core';
import { IconQuote } from '@tabler/icons-react';
import { YouTubeEmbed } from '@/app/components/youtube/YouTubeEmbed';

const icon = <IconQuote />;

const preset = presetReact.extend((tags, options) => ({
  ...tags,
  video: (node) => ({
    tag: 'div',
    content: [{ tag: YouTubeEmbed, attrs: { video: node.content } }],
    attrs: { style: { maxWidth: '640px', margin: '15px 0' } },
  }),
  quote: (node) => ({
    tag: Blockquote,
    content: node.content,
    attrs: { icon },
  }),
  code: (node) => ({
    tag: Code,
    content: node.content,
  }),
  img: (node) => ({
    tag: Image,
    attrs: {
      src: node.content,
      alt: 'image',
      w: 'auto',
      fit: 'contain',
      style: { maxWidth: '640px', margin: '10px 0' },
    },
  }),
  h1: (node) => ({
    tag: Title,
    content: node.content,
    attrs: {
      order: 1,
    },
  }),
  h2: (node) => ({
    tag: Title,
    content: node.content,
    attrs: {
      order: 2,
    },
  }),
  h3: (node) => ({
    tag: Title,
    content: node.content,
    attrs: {
      order: 3,
    },
  }),
  h4: (node) => ({
    tag: Title,
    content: node.content,
    attrs: {
      order: 4,
    },
  }),
  h5: (node) => ({
    tag: Title,
    content: node.content,
    attrs: {
      order: 5,
    },
  }),
  h6: (node) => ({
    tag: Title,
    content: node.content,
    attrs: {
      order: 6,
    },
  }),
  hr: () => ({
    tag: Divider,
  }),
  url: (node) => ({
    tag: Link,
    content: node.content,
    attrs: {
      //@ts-ignore work
      href: Object.values(node.attrs)[0],
    },
  }),
  ul: (node) => ({
    tag: List,
    content: node.content,
  }),
  ol: (node) => ({
    tag: List,
    content: node.content,
    attrs: {
      type: 'ordered',
    },
  }),
  li: (node) => ({
    tag: List.Item,
    content: node.content,
  }),
  table: (node) => ({
    tag: Table,
    content: node.content,
  }),
  tr: (node) => ({
    tag: Table.Tr,
    content: node.content,
  }),
  th: (node) => ({
    tag: Table.Th,
    content: node.content,
  }),
  td: (node) => ({
    tag: Table.Td,
    content: node.content,
  }),
  thead: (node) => ({
    tag: Table.Thead,
    content: node.content,
  }),
  tbody: (node) => ({
    tag: Table.Tbody,
    content: node.content,
  }),
  tfoot: (node) => ({
    tag: Table.Tfoot,
    content: node.content,
  }),
  left: (node) => ({
    tag: Text,
    attrs: {
      ta: 'left',
    },
    content: node.content,
  }),
  center: (node) => ({
    tag: Text,
    attrs: {
      ta: 'center',
    },
    content: node.content,
  }),
  right: (node) => ({
    tag: Text,
    attrs: {
      ta: 'right',
    },
    content: node.content,
  }),
}));

const plugins = [preset()];

export const BBCode = ({ children }: { children: string }) => (
  <BB
    plugins={plugins}
    options={{
      onlyAllowTags: [
        'b',
        'i',
        'u',
        's',
        'sup',
        'sub',
        'hr',
        'br',
        'left',
        'center',
        'right',
        'url',
        'img',
        'video',
        'code',
        'quote',
        'ul',
        'ol',
        'li',
        'table',
        'tbody',
        'tfoot',
        'td',
        'th',
        'tr',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ],
    }}
  >
    {children
      .replace(/\n/g, '[br]')
      .replace(/\[br]\s+\[/g, '[br][')
      .replace(/]\s+\[br]/g, '][br]')
      .replace('[br][quote]', '[quote]')
      .replace('[/quote][br]', '[/quote]')
      .replace('[br][code]', '[code]')
      .replace('[/code][br]', '[/code]')
      .replace('[br][video]', '[video]')
      .replace('[/video][br]', '[/video]')
      .replace('[br][img]', '[img]')
      .replace('[/img][br]', '[/img]')}
  </BB>
);

export default BBCode;
